import {
  BorderGridContainer,
  Col,
  ContentTiles,
  GridBox,
  H1,
  Hidden,
  Image,
  PortableText,
  Small,
  SpacerWrapper,
} from '@ahmdigital/ui';
import { isString, startsWith } from 'lodash/fp';
import classNames from 'classnames';
import portableTextToPlain from '@ahmdigital/logic/lib/content/utils/portable-text-to-plain';
import type { ComponentProps } from 'react';

import { GetPerkQuery } from '../../../../../types/__codegen__/sanity/graphql';
import { PERKS_PAGE_TITLE_TEMPLATE } from '../../../../../ahm-constants/ui';
import formatReleaseEndDate from '../../utils/format-release-end-date';
import getImageAttributes from '../../../../../cms/utils/get-image-attributes';
import Head from '../../../../../../utils/head';
import imageUrlBuilder from '../../../../../cms/utils/image-url-builder';
import styles from '../../styles.module.sass';
import TermsAndConditions from '../../../../../components/terms-and-conditions';
import TermsSection from '../../../../../components/sections/terms-section';
import VoucherCallout from '../voucher-callout';

type PerksDetailContentProps = {
  perk: GetPerkQuery['allMemberPerk'][number];
  queryMagicLinkCode?: string;
};

const PerksDetailContent = ({ perk, queryMagicLinkCode }: PerksDetailContentProps) => {
  const { descriptionRaw, release, name, service } = perk;
  const releaseEndDate = release?.endTime;

  const heroImage = (
    <Image
      // @ts-expect-error - Automatic, Please fix when editing this file
      src={imageUrlBuilder(perk.imageObject?.image).width(undefined).height(1344).url()}
      className={styles.heroImage}
      alt={`${perk.offeredBy?.name} hero image`}
    />
  );

  const logo = perk.offeredBy?.logo;
  // @ts-expect-error - Automatic, Please fix when editing this file
  const linkIcon = !startsWith('/', perk.service?.cta?.url)
    ? ({ name: 'external-link-light', position: 'right' } as const)
    : null;

  const showDisclaimerInService = Boolean(service);
  const hasCta = Boolean(service?.urlObject?.url);
  const serviceTile = {
    ctas: hasCta
      ? [
          {
            icon: linkIcon,
            link: service?.urlObject?.url,
            linkText: `${service?.urlObject?.name}${perk.disclaimer ? perk.disclaimer?.symbol : ''}`,
            variant: 'black',
          },
        ]
      : undefined,
    footer: (
      <Small className={styles.flexRow}>
        {perk.disclaimer?.symbol}
        <PortableText source={perk.disclaimer?.contentRaw} />
      </Small>
    ),
    image: getImageAttributes({ height: 240, imageObject: service?.imageObject }),
    subtitle: service?.summary,
    title: service?.name,
    variant: 'fullWidth',
  } as ComponentProps<typeof ContentTiles>['tiles'][number];

  return (
    <>
      {perk.metadataObject ? (
        <Head titleTemplate={PERKS_PAGE_TITLE_TEMPLATE}>
          <title>{perk.metadataObject.title}</title>
          <meta name="description" content={portableTextToPlain(perk.metadataObject.description)} />
          <meta name="og:description" content={portableTextToPlain(perk.metadataObject.description)} />
          <meta name="twitter:description" content={portableTextToPlain(perk.metadataObject.description)} />
        </Head>
      ) : null}
      <BorderGridContainer hideBorderTop>
        <GridBox padding="0.5rem">
          <SpacerWrapper variant={['paddingBottomSpacer3', 'paddingTopSpacer3']}>
            <section className={styles.flexRow}>
              <Col df={6} dfOffset={1} md={12} mdOffset={0} smOffset={0}>
                {logo && (
                  <SpacerWrapper variant={['marginBottomSpacer7', styles.flexRow]}>
                    <Image
                      // @ts-expect-error - Automatic, Please fix when editing this file
                      src={imageUrlBuilder(logo).width(100).height(undefined).url()}
                      alt={`${perk.offeredBy?.name} logo`}
                      className={styles.logoImage}
                    />
                  </SpacerWrapper>
                )}
                <Hidden df lg>
                  <SpacerWrapper variant="marginBottomSpacer11">
                    <div className={styles.flexRow}>{heroImage}</div>
                  </SpacerWrapper>
                </Hidden>
                <H1 variant="marginBottomSpacer7" looksLike="h2">
                  {name}
                </H1>
                {isString(releaseEndDate) && (
                  <SpacerWrapper variant="paddingBottomSpacer8">
                    <Small variant="small14">Offer expires on the {formatReleaseEndDate(releaseEndDate)}.</Small>
                  </SpacerWrapper>
                )}

                <PortableText variant="marginBottomSpacer8" source={descriptionRaw} />
                <VoucherCallout
                  showDisclaimerText={!showDisclaimerInService}
                  perk={perk}
                  queryMagicLinkCode={queryMagicLinkCode}
                />
              </Col>

              <div className={classNames(styles.flexRow, styles.imageWrapper)}>{heroImage}</div>
            </section>
          </SpacerWrapper>
        </GridBox>
      </BorderGridContainer>

      {service ? (
        <BorderGridContainer>
          <ContentTiles tiles={[serviceTile]} />
        </BorderGridContainer>
      ) : null}

      {perk.termsAndConditionsRaw && (
        <TermsSection
          termsComponent={<TermsAndConditions terms={[{ descriptionRaw: perk.termsAndConditionsRaw }]} />}
        />
      )}
    </>
  );
};

export default PerksDetailContent;
